import { useMemo } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Activity, Clock, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { scoreActivities, type ActivityScore } from "@/lib/insights/activity-scores";
import {
  findBestWindow,
  formatWindowRange,
  type HourlyHour,
} from "@/lib/insights/best-window";

interface ActivityPanelProps {
  current: {
    temp: number;
    feels_like: number;
    humidity: number;
    wind_speed: number;
    uvi?: number;
    clouds?: number;
    weather?: { id: number; icon: string; main: string }[];
  };
  hourly?: HourlyHour[];
  aqi?: { list?: { main?: { aqi?: number } }[] };
}

function scoreColor(s: number): { ring: string; text: string; bar: string; bg: string } {
  if (s >= 8) return { ring: "ring-emerald-500/30", text: "text-emerald-400", bar: "bg-emerald-400", bg: "bg-emerald-500/10" };
  if (s >= 6) return { ring: "ring-lime-500/30", text: "text-lime-400", bar: "bg-lime-400", bg: "bg-lime-500/10" };
  if (s >= 4) return { ring: "ring-amber-500/30", text: "text-amber-400", bar: "bg-amber-400", bg: "bg-amber-500/10" };
  if (s >= 2) return { ring: "ring-orange-500/30", text: "text-orange-400", bar: "bg-orange-400", bg: "bg-orange-500/10" };
  return { ring: "ring-rose-500/30", text: "text-rose-400", bar: "bg-rose-400", bg: "bg-rose-500/10" };
}

export function ActivityPanel({ current, hourly, aqi }: ActivityPanelProps) {
  const aqiVal = aqi?.list?.[0]?.main?.aqi;
  const isDay = current.weather?.[0]?.icon?.endsWith("d") ?? true;

  const scores = useMemo<ActivityScore[]>(() => {
    return scoreActivities({
      tempC: current.temp,
      feelsLikeC: current.feels_like,
      humidity: current.humidity,
      windMs: current.wind_speed,
      uvi: current.uvi ?? 0,
      aqi: aqiVal,
      cloudPct: current.clouds,
      isDay,
      precipMm: 0, // rough — we use hourly window for precip-aware best block
    });
  }, [current, aqiVal, isDay]);

  const bestWindow = useMemo(() => findBestWindow(hourly), [hourly]);
  const top = scores.reduce((a, b) => (a.score >= b.score ? a : b));

  return (
    <GlassPanel intensity="low" className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
            <Activity className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight">Activity Scores</h3>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Right-now ratings · 0–10
            </p>
          </div>
        </div>
        {bestWindow && (
          <div className="hidden sm:flex flex-col items-end">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Best outdoor window
            </span>
            <span className="text-xs font-semibold inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3 text-primary" />
              {formatWindowRange(bestWindow.startTs, bestWindow.endTs)}
              <span className="text-[10px] text-muted-foreground">
                · {bestWindow.score}/10
              </span>
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {scores.map((a) => {
          const c = scoreColor(a.score);
          const pct = a.score * 10;
          return (
            <div
              key={a.key}
              className={cn(
                "group/act rounded-xl border border-border/40 bg-background/30 p-3 hover:border-border/70 transition-colors",
                a.key === top.key && "ring-1 ring-inset",
                a.key === top.key && c.ring,
              )}
              title={a.reason}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xl" role="img" aria-label={a.label}>
                  {a.emoji}
                </span>
                <span className={cn("text-base font-bold tabular-nums", c.text)}>
                  {a.score.toFixed(1)}
                </span>
              </div>
              <p className="mt-1 text-xs font-semibold tracking-tight">{a.label}</p>
              <div className="mt-2 h-1 rounded-full bg-border/40 overflow-hidden">
                <div
                  className={cn("h-full rounded-full", c.bar)}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="mt-1.5 text-[10px] leading-tight text-muted-foreground line-clamp-2">
                {a.reason}
              </p>
            </div>
          );
        })}
      </div>

      {bestWindow && (
        <div className="sm:hidden mt-3 flex items-center gap-2 rounded-lg bg-primary/5 border border-primary/15 px-3 py-2">
          <Clock className="h-3.5 w-3.5 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Best outdoor window
            </p>
            <p className="text-xs font-semibold">
              {formatWindowRange(bestWindow.startTs, bestWindow.endTs)}{" "}
              <span className="text-muted-foreground">· {bestWindow.score}/10</span>
            </p>
          </div>
        </div>
      )}

      <p className="mt-3 text-[10px] text-muted-foreground inline-flex items-center gap-1.5">
        <Sparkles className="h-2.5 w-2.5 text-primary" />
        Top pick:{" "}
        <span className="font-semibold text-foreground/80">
          {top.label} {top.score.toFixed(1)}/10
        </span>
        {bestWindow && (
          <>
            {" "}
            ·{" "}
            <span className="text-muted-foreground">
              best window {formatWindowRange(bestWindow.startTs, bestWindow.endTs)}
            </span>
          </>
        )}
      </p>
    </GlassPanel>
  );
}
